/**
 * lib/loanList.ts
 * ------------------------------------------------------------------
 * HR LOANS — how the Loans & Advances lists are arranged (Oct 2026).
 *
 * The list used to show every record of a type, one row each. A teacher
 * whose loans had been consolidated then appeared two or three times: the
 * consolidated loan, and the old loans folded into it (kept on file,
 * archived, `values.consolidatedInto` pointing at the new one). The school
 * asked for one amount per teacher, and the old ones apart.
 *
 *   current  one row per member of staff: their active (or suspended)
 *            records, totalled — monthly amount and remaining balance.
 *            Two genuinely separate loans still show as one row, "2 loans",
 *            opening to each.
 *   old      the records that are not running: folded into a consolidated
 *            loan, paid off, archived — or fully recovered by the pay runs
 *            though still marked active (Duduzani Dhlamini's August advance;
 *            judged on the DERIVED balance, not the stored one, which goes
 *            stale).
 * ------------------------------------------------------------------ */
import { deductionCurrentBalance, deductionMonthlyAmount, deductionOpeningBalance, round2, type HrDeduction } from '@/lib/hrPayroll';

/**
 * A finite loan the pay runs have fully recovered — judged at the end of
 * LAST month. The current month's balance already counts this month's
 * instalment as taken, and this month's payroll may not have run: on 26 Oct
 * 2026 that would have called Andrew Jones's October advance "repaid".
 */
function repaid(d: HrDeduction, asOf: Date): boolean {
  if (deductionOpeningBalance(d) <= 0) return false;
  const last = { year: asOf.getMonth() === 0 ? asOf.getFullYear() - 1 : asOf.getFullYear(), month: asOf.getMonth() === 0 ? 12 : asOf.getMonth() };
  const bal = deductionCurrentBalance(d, last);
  return bal !== null && bal <= 0;
}

export interface StaffLoanRow {
  staffId: string;
  records: HrDeduction[];
  monthly: number;
  /** null when none of the records carries a balance. */
  remaining: number | null;
}

/** Not running: folded into another loan, paid off, or archived. */
export function isOldLoan(d: HrDeduction, asOf = new Date()): boolean {
  return !!(d.values as Record<string, unknown>)?.consolidatedInto || d.status === 'archived' || d.status === 'paid_off' || repaid(d, asOf);
}

/** Why an old record is old, in words for the list. */
export function oldLoanReason(d: HrDeduction): string {
  const v = (d.values ?? {}) as Record<string, unknown>;
  if (v.consolidatedInto) {
    const at = String(v.consolidatedAt ?? '').slice(0, 10);
    return `Folded into a consolidated loan${at ? ` on ${new Date(`${at}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}`;
  }
  if (d.status === 'paid_off') return 'Paid off';
  return d.status === 'archived' ? 'Archived' : 'Repaid';
}

export function arrangeLoans(records: HrDeduction[], asOf = new Date()): { current: StaffLoanRow[]; old: HrDeduction[] } {
  const old = records.filter((d) => isOldLoan(d, asOf));
  const byStaff = new Map<string, HrDeduction[]>();
  for (const d of records) {
    if (isOldLoan(d, asOf)) continue;
    (byStaff.get(d.staffId) ?? byStaff.set(d.staffId, []).get(d.staffId)!).push(d);
  }
  const current = [...byStaff].map(([staffId, recs]) => {
    const balances = recs.map((d) => deductionCurrentBalance(d));
    return {
      staffId,
      records: recs,
      monthly: round2(recs.reduce((t, d) => t + deductionMonthlyAmount(d), 0)),
      remaining: balances.every((b) => b == null) ? null : round2(balances.reduce((t: number, b) => t + (b ?? 0), 0)),
    };
  });
  return { current, old };
}
